import React, { useContext, useState } from 'react';

import { notification, Popover, Radio } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getMoment, getNow, showDateNoTime } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';
import { useUserPermission } from '@contexts/UserContext';

import { forceReloadInvoice, useClearInvoiceConfirm } from '@hooks/API/UseInvoices';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwButton, NwLinkButton, NwRemoveLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import EditPaymentStatusDrawer from '@components/InvoicesAndCreditNotes/InvoicePage/Drawers/EditPaymentStatusDrawer';
import InvoiceConfirmDrawer from '@components/InvoicesAndCreditNotes/InvoicePage/Drawers/InvoiceConfirmDrawer';

import EditBankDetailsDrawer from '../Common/EditBankDetailsDrawer';
import EditPaymentTermsDrawer from '../Common/EditPaymentTermsDrawer';
// import PrintInvoiceButton from '../PrintInvoiceButton';
import PrintEditableInvoiceButton from '../PrintEditableInvoiceButton';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    padding: 1rem 0 2rem 0;

    .invoice-top-box {
        display: flex;
        padding-right: 2rem;
        align-items: center;
        justify-content: flex-start;
        flex: 1 1 0;

        .invoice-top-box-icon {
            min-width: 6rem;
            text-align: center;
            font-size: 2rem;
        }

        .invoice-top-box-content {
            .invoice-top-box-content-title {
                font-weight: bold;
                font-size: ${(props) => props.theme.FontSizeLg};
            }
            .invoice-top-box-content-label {
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
            }
            p {
                margin: 0;

                &.overdue {
                    background-color: ${(props) => props.theme.DangerColor};
                    color: #fff;
                    padding: 0.2rem 0.5rem;
                }
            }
            small {
                max-width: 13rem;
                font-size: ${(props) => props.theme.FontSizeSm};
                display: block;
                opacity: 0.5;
                line-height: 1.1em;
            }
        }
    }

    .invoice-top-export {
        display: flex;
        min-width: 12rem;
        gap: 2rem;
    }

    .confirmation-payment-and-export {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-bottom: 2rem;
    }

    .terms-and-bank-account {
        display: flex;
        flex-direction: row;
        width: 100%;
    }
`;

const ElectronicInvoicePopoverContent = styled.div`
    div.company-info {
        padding: 0.5rem 0;
        cursor: pointer;
        border-bottom: ${(props) => props.theme.DefaultBorder};

        &:hover {
            background-color: ${(props) => props.theme.HoverablesBackground};
        }
    }
    div.model-names-switch {
        padding: 1rem 0;
        font-size: ${(props) => props.theme.FontSizeSm};
    }
    div.e-invoice-button-wrapper {
        padding: 1rem 0;
        font-size: ${(props) => props.theme.FontSizeSm};
    }
`;

const InvoicePageTop = ({ invoiceData, locked }) => {
    const paymentStatusOption = [
        { label: 'NOT PAID', value: 'NotPaid', icon: byPrefixAndName.fasl['hourglass-empty'] },
        { label: 'PARTIALLY PAID', value: 'PartiallyPaid', icon: byPrefixAndName.fasl['envelope-open-dollar'] },
        { label: 'PAID', value: 'Paid', icon: byPrefixAndName.fasl['money-check-dollar'] },
    ];

    const [ModelNamesInElectronicInvoice, setModelNamesInElectronicInvoice] = useState(true);

    //get configurations and see if taxmethod is italian
    const { cachedList } = useContext(ListContext);
    const taxMethod = cachedList.taxmethods.items.Method;
    const isIT = taxMethod.toLowerCase() === 'italian';
    let useElectronicInvoice = false;
    const configurations = useConfigurations();

    let firstCompanyInfoCode = '';
    const CompanyInfosForElectronicInvoice = [];
    if (isIT) {
        if (configurations && configurations.CompanyInfos && configurations.CompanyInfos.length > 0) {
            const companyInfos = configurations.CompanyInfos;
            if (companyInfos.length > 0) {
                CompanyInfosForElectronicInvoice.push(...companyInfos);
            }
            firstCompanyInfoCode = CompanyInfosForElectronicInvoice[0].Code;
        }
        if (CompanyInfosForElectronicInvoice.length > 0) {
            useElectronicInvoice = true;
        }
    }
    const [selectedCompanyInfoCode, setSelectedCompanyInfoCode] = useState(firstCompanyInfoCode);

    const canPost = useUserPermission('PostInvoice');

    const [showEditPaymentStatusDrawer, setShowEditPaymentStatusDrawer] = useState(false);
    const [showInvoiceConfirmDrawer, setShowInvoiceConfirmDrawer] = useState(false);
    const [showEditPaymentTermsDrawer, setShowEditPaymentTermsDrawer] = useState(false);
    const [showEditBankDrawer, setShowEditBankDrawer] = useState(false);

    const { mutateAsync: onClearInvoiceConfirm } = useClearInvoiceConfirm();

    const onClearInvoice = async () => {
        try {
            await onClearInvoiceConfirm(invoiceData.ID);
        } catch (e) {
            notification.error({
                message: 'Error unconfirming invoice',
                description: e?.message || 'Unknown error',
            });
        }
    };

    let selectedOption = paymentStatusOption.find((item) => item.value === invoiceData.PaymentStatusName);
    if (!selectedOption) {
        selectedOption = paymentStatusOption[0];
    }

    const invoiceDataPaymentDate = invoiceData.PaymentDate ? invoiceData.PaymentDate : null;
    const invoiceDataPaymentDueDate = invoiceData.PaymentDueDate ? invoiceData.PaymentDueDate : null;

    const isOverDue = () => {
        //if invoice is not paid and payment due date is in the past
        return !isPaid() && getMoment(invoiceDataPaymentDueDate).isBefore(getNow(), 'day');
    };

    const isPaid = () => {
        return invoiceData.PaymentStatusName === 'Paid';
    };

    const setAsReadyToPost = async () => {
        await Axios.put(`/Invoices/SetReadyToPost`, { InvoiceID: invoiceData.ID, ReadyToPost: true });
        forceReloadInvoice(invoiceData.ID);
    };

    const onCreateElectronicInvoice = async () => {
        const selectedCompanyInfo = CompanyInfosForElectronicInvoice.find(
            (item) => item.Code === selectedCompanyInfoCode
        );
        const invoiceNumber = invoiceData.Number.padStart(5, '0');

        Axios.post(
            '/invoices/electronicinvoice',
            {
                InvoiceID: invoiceData.ID,
                CompanyInfoCode: selectedCompanyInfoCode,
                IncludeName: ModelNamesInElectronicInvoice,
            },
            { responseType: 'blob', timeout: 30000 }
        )
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = `${selectedCompanyInfo.PaeseIva}${selectedCompanyInfo.PartitaIVA}_${invoiceNumber}.xml`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error generating electronic invoice',
                    description: error.message,
                });
            });
    };

    return (
        <Wrapper>
            <div className="confirmation-payment-and-export">
                <div className="invoice-top-box">
                    <div className="invoice-top-box-icon">
                        <NwIcon
                            icon={byPrefixAndName.fasl['file-invoice']}
                            color={invoiceData.Number === null ? StyleVariables.DangerColor : '#000'}
                        />
                    </div>
                    {!invoiceData.Number ? (
                        <div className="invoice-top-box-content">
                            <div className="invoice-top-box-content-title">Draft</div>
                            <NwLinkButton
                                disabled={locked}
                                onClick={() => setShowInvoiceConfirmDrawer(true)}
                                label="assign invoice number and date"
                            />
                        </div>
                    ) : (
                        <div className="invoice-top-box-content">
                            <div className="invoice-top-box-content-title">
                                {invoiceData.Number} / {getMoment(invoiceData.Date).year()}{' '}
                            </div>
                            <div>Invoice date: {getMoment(invoiceData.Date).format('DD MMM YYYY')} </div>
                            <NwRemoveLinkButton
                                needConfirmation={true}
                                confirmationLabel="You are reverting the invoice to draft. The invoice number and invoice date will be cleared."
                                confirmationTitle="PLEASE CONFIRM"
                                confirmationButtonLabel="REVERT TO DRAFT"
                                confirmationButtonIcon={byPrefixAndName.fasl['undo']}
                                icon={byPrefixAndName.fasl['undo']}
                                disabled={locked}
                                onClick={() => onClearInvoice()}
                                label="revert to draft"
                            />
                        </div>
                    )}
                </div>
                <div className="invoice-top-box">
                    <div className="invoice-top-box-icon">
                        <NwIcon
                            icon={selectedOption.icon}
                            opacity={!invoiceData.Number ? 0.25 : 1}
                            color={isOverDue() ? StyleVariables.DangerColor : '#000'}
                        />
                    </div>
                    <div className="invoice-top-box-content">
                        <div className="invoice-top-box-content-title">{selectedOption.label}</div>
                        {isPaid() && invoiceDataPaymentDate && (
                            <p>
                                Payment date: <strong>{showDateNoTime(invoiceDataPaymentDate)}</strong>
                            </p>
                        )}
                        {
                            !invoiceData.Number ? (
                                <small>You can change payment status only for confirmed invoice</small>
                            ) : (
                                <NwLinkButton
                                    disabled={!invoiceData.Number || locked}
                                    onClick={() => setShowEditPaymentStatusDrawer(true)}
                                    label="change payment status"
                                />
                            )
                            // disabled={!invoiceData.Number || locked} - removed for Elite
                        }
                    </div>
                </div>
                <div className="invoice-top-export">
                    <div className="invoice-top-export-icon">
                        {/* <PrintInvoiceButton
                            invoiceID={invoiceData.ID}
                            invoiceType={invoiceData.TypeName}
                            placement="leftTop"
                        /> */}
                        <PrintEditableInvoiceButton
                            invoiceID={invoiceData.ID}
                            invoiceType={invoiceData.TypeName}
                            placement="leftTop"
                        />
                    </div>
                    <div className="invoice-top-export-icon">
                        <NwIcon
                            disabled={locked || !canPost}
                            size="2x"
                            icon={byPrefixAndName.fasl['file-export']}
                            onClick={setAsReadyToPost}
                            tooltip="set this invoice as ready to post"
                            pointer
                        />
                    </div>
                    {useElectronicInvoice &&
                        // (CompanyInfosForElectronicInvoice.length > 1 ? (
                        (!invoiceData.Number ? (
                            <div className="invoice-top-export-icon">
                                <NwIcon
                                    disabled
                                    size="2x"
                                    icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                    tooltip="you can create e-invoice only for confirmed invoices"
                                />
                            </div>
                        ) : (
                            <div className="invoice-top-export-icon">
                                <Popover
                                    trigger="click"
                                    placement="left"
                                    title="Create e-invoice"
                                    content={
                                        <ElectronicInvoicePopoverContent>
                                            {CompanyInfosForElectronicInvoice.length > 1 ? (
                                                CompanyInfosForElectronicInvoice.map((companyInfo) => (
                                                    <div className="company-info" key={companyInfo.Code}>
                                                        <Radio
                                                            checked={selectedCompanyInfoCode === companyInfo.Code}
                                                            onChange={() =>
                                                                setSelectedCompanyInfoCode(companyInfo.Code)
                                                            }
                                                        >
                                                            {`${companyInfo.Code} • ${companyInfo.Nome}`}
                                                        </Radio>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="company-info">
                                                    {`${CompanyInfosForElectronicInvoice[0].Code} • ${CompanyInfosForElectronicInvoice[0].Nome}`}
                                                </div>
                                            )}
                                            <div className="model-names-switch">
                                                <CustomSwitch
                                                    text="Include model names in e-invoice lines"
                                                    checked={ModelNamesInElectronicInvoice}
                                                    onChange={(checked) => setModelNamesInElectronicInvoice(checked)}
                                                />
                                            </div>
                                            <div className="e-invoice-button-wrapper">
                                                <NwButton
                                                    primary
                                                    icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                                    onClick={onCreateElectronicInvoice}
                                                    label="create e-invoice"
                                                />
                                            </div>
                                        </ElectronicInvoicePopoverContent>
                                    }
                                >
                                    <NwIcon size="2x" icon={byPrefixAndName.fasl['cloud-arrow-down']} pointer />
                                </Popover>
                            </div>
                        ))}
                </div>
            </div>
            <div className="terms-and-bank-account">
                {/* payment terms box */}
                <div className="invoice-top-box">
                    <div className="invoice-top-box-icon">
                        <NwIcon
                            icon={byPrefixAndName.fasl['handshake']}
                            color={isOverDue() ? StyleVariables.DangerColor : '#000'}
                        />
                    </div>
                    <div className="invoice-top-box-content">
                        <div className="invoice-top-box-content-label">PAYMENT TERMS</div>
                        <p>
                            <strong>{invoiceData.PaymentTerm.Name}</strong>
                        </p>
                        {invoiceDataPaymentDueDate && (
                            <p className={isOverDue() ? 'overdue' : ''}>
                                Due Date: {showDateNoTime(invoiceDataPaymentDueDate)}
                            </p>
                        )}
                        <NwLinkButton
                            disabled={locked}
                            onClick={() => setShowEditPaymentTermsDrawer(true)}
                            label="change payment terms"
                        />
                    </div>
                </div>
                {/* bank account box */}
                <div className="invoice-top-box">
                    <div className="invoice-top-box-icon">
                        <NwIcon icon={byPrefixAndName.fasl['building-columns']} />
                    </div>
                    <div className="invoice-top-box-content">
                        <Popover
                            content={
                                <>
                                    <p>
                                        <strong>{invoiceData.BankAccount.BankName}</strong>
                                    </p>
                                    <p>Holder: {invoiceData.BankAccount.Holder}</p>
                                    <p>IBAN: {invoiceData.BankAccount.IBAN}</p>
                                    <p>BIC/SWIFT: {invoiceData.BankAccount.SWIFT}</p>
                                </>
                            }
                            title="Bank account details"
                        >
                            <div className="invoice-top-box-content-label">BANK ACCOUNT</div>
                            <p>
                                <strong>{invoiceData.BankAccount.BankName}</strong>
                            </p>
                            <p>Iban: {invoiceData.BankAccount.IBAN}</p>
                        </Popover>
                        <NwLinkButton
                            disabled={locked}
                            onClick={() => setShowEditBankDrawer(true)}
                            label="change bank details"
                        />
                    </div>
                </div>
                <div className="invoice-top-export">&nbsp;</div>
            </div>
            {showEditPaymentStatusDrawer && (
                <EditPaymentStatusDrawer
                    invoiceData={invoiceData}
                    onClose={() => setShowEditPaymentStatusDrawer(false)}
                />
            )}
            {showInvoiceConfirmDrawer && (
                <InvoiceConfirmDrawer invoiceID={invoiceData.ID} onClose={() => setShowInvoiceConfirmDrawer(false)} />
            )}
            {showEditPaymentTermsDrawer && (
                <EditPaymentTermsDrawer data={invoiceData} onClose={() => setShowEditPaymentTermsDrawer(false)} />
            )}
            {showEditBankDrawer && (
                <EditBankDetailsDrawer data={invoiceData} onClose={() => setShowEditBankDrawer(false)} />
            )}
        </Wrapper>
    );
};

export default InvoicePageTop;
